import { useState, ReactNode, SyntheticEvent, useRef, useEffect, useCallback } from 'react';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { useMediaQuery, useTheme } from '@mui/material';

import { TTabsSize, TTabRowVariant, Maybe } from '@/types';
import { useAppContext } from '@/context';
import { DESKTOP_HEADER_ID, DESKTOP_HEADER_ID_WITH_NAV_MENU, MOBILE_HEADER_ID } from '@/constants';
import { getMinHeaderHeightStyles } from '@/components/Header/Header.styles';

import {
	tabsContentStyles,
	getTabsListWrapStyles,
	getTabsWrapperStyles,
	tabsStyles,
	getTabStyles,
} from './TeamTAGTabs.styles';

const a11yProps = (value: string) => {
	return {
		id: `tab-${value}`,
		'aria-controls': `tabpanel-${value}`,
	};
};

interface TAGTabsProps {
	tabs: {
		label: string;
		value: string;
		href?: string;
		jumplinkName?: string;
		content: ReactNode;
	}[];
	role?: 'default' | 'navigation';
	centered?: boolean;
	dataTestId?: string;
	tabRowVariant?: TTabRowVariant;
	tabsSize?: TTabsSize;
	jumplinkTabs?: Maybe<boolean>;
}

export default function TeamTAGTabs({
	tabs,
	role = 'default',
	centered,
	dataTestId = '',
	tabRowVariant = 'Standard',
	tabsSize = 'Small',
	jumplinkTabs = false,
}: Readonly<TAGTabsProps>) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const isBelowMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
	const [currentValue, setCurrentValue] = useState<null | string>(jumplinkTabs ? null : tabs[0].value);
	const [isTabsFixed, setIsTabsFixed] = useState(false);
	const [activeColor, setActiveColor] = useState('#2288C9'); // Default color for the active tab
	const [_, setForceRender] = useState(false);
	const [isTabsVisible, setIsTabsVisible] = useState(true); // New state for visibility
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Ref to hold the timeout ID
	const tabsWrapNode = useRef<HTMLDialogElement>(null);

	const { config } = useAppContext();
	const showMobileHeaderLocationBar = config?.featureFlags?.showMobileHeaderLocationBar;
	const showHeaderWithLocationNav = config.featureFlags?.showHeaderWithLocationNav;
	const showHeaderNavigationMenu = config.featureFlags?.showHeaderNavigationMenu;
	const withDisappearingHeader = config.features?.header?.hideHeaderOnScroll;

	const desktopHeaderElement = globalThis?.document?.getElementById(
		withDisappearingHeader ? DESKTOP_HEADER_ID_WITH_NAV_MENU : DESKTOP_HEADER_ID
	);
	const mobileHeaderElement = globalThis?.document?.getElementById(MOBILE_HEADER_ID);

	const desktopHeaderDisappearingTopStyle = desktopHeaderElement?.style?.top;
	const mobileHeaderDisappearingTopStyle = mobileHeaderElement?.style?.top;

	const handleChange = (_: SyntheticEvent, newValue: string) => {
		setCurrentValue(newValue);
	};

	const handleJumplink = (jumplinkName: string) => {
		if (jumplinkName && window) {
			window.location.hash = jumplinkName;
		}
	};

	const sectionColorsRef = useRef<Record<string, string>>({
		'aspen-dental': '#2288C9',
		chapter: '#CE0059',
		clearchoice: '#F99D25',
		wellnow: '#CF2A2A',
		lovet: '#39863C',
	});

	const tabValuesRef = useRef(['aspen-dental', 'chapter', 'clearchoice', 'wellnow', 'lovet']);

	const handleScroll = useCallback(() => {
		const sectionColors = sectionColorsRef.current;
		const tabValues = tabValuesRef.current;
		if (tabsWrapNode.current) {
			const blockOffsetTop = tabsWrapNode.current.offsetTop;
			if (window.scrollY >= blockOffsetTop) {
				setIsTabsFixed(true);

				const observer = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {
							if (entry.isIntersecting) {
								const sectionId = entry.target.id;
								// Update the state with the new color
								setCurrentValue(tabs[tabValues.findIndex((el) => el === sectionId)].value);
								setActiveColor(sectionColors[sectionId]);
							}
						});
					},
					{ rootMargin: '0px 0px -50% 0px', threshold: 0.1 }
				);

				const sections = document.querySelectorAll('#aspen-dental, #chapter, #clearchoice, #wellnow, #lovet');
				sections.forEach((section) => observer.observe(section));

				setForceRender((prev) => !prev);
				return () => sections.forEach((section) => observer.unobserve(section));
			} else {
				setIsTabsFixed(false);
			}
		}
	}, [sectionColorsRef, tabValuesRef, tabs]);

	useEffect(() => {
		if (role !== 'navigation') return;
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll, role]);

	const headerPositioning = getMinHeaderHeightStyles(
		showMobileHeaderLocationBar,
		showHeaderNavigationMenu,
		showHeaderWithLocationNav
	);

	const handleMouseMove = useCallback(() => {
		setIsTabsVisible(true); // Always show tabs on mouse move

		// Clear any previous timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		// Set a new timeout to hide the tabs after 2 seconds
		timeoutRef.current = setTimeout(() => {
			setIsTabsVisible(false);
		}, 2000);
	}, []);

	useEffect(() => {
		// Shared timeout reference for both mouse and scroll logic
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		// Logic for mobile (small screen)
		if (isSmallScreen) {
			const handleScrollStop = () => {
				// Show tabs when scrolling starts
				setIsTabsVisible(true);

				// Clear the existing timeout
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}

				// Set a new timeout to hide tabs if scrolling stops
				timeoutRef.current = setTimeout(() => {
					setIsTabsVisible(false);
				}, 2000);
			};

			if (isTabsFixed) {
				window.addEventListener('scroll', handleScrollStop);
				// Initially hide after a delay if the user doesn't scroll
				timeoutRef.current = setTimeout(() => {
					setIsTabsVisible(false);
				}, 2000);
			} else {
				// Cleanup: ensure tabs are visible and listener is removed
				setIsTabsVisible(true);
				window.removeEventListener('scroll', handleScrollStop);
			}

			// Cleanup function for the effect
			return () => window.removeEventListener('scroll', handleScrollStop);
		}
		// Logic for desktop (large screen)
		else {
			if (isTabsFixed) {
				window.addEventListener('mousemove', handleMouseMove);
				timeoutRef.current = setTimeout(() => {
					setIsTabsVisible(false);
				}, 2000);
			} else {
				window.removeEventListener('mousemove', handleMouseMove);
				setIsTabsVisible(true);
			}

			return () => window.removeEventListener('mousemove', handleMouseMove);
		}
	}, [isTabsFixed, handleMouseMove, isSmallScreen]);

	return (
		<Box
			sx={tabsContentStyles}
			style={{
				visibility: isTabsFixed && !isTabsVisible ? 'hidden' : 'visible',
				transition: 'visibility 0.3s ease',
			}}
			ref={tabsWrapNode}
			data-test-id={'tag_tabs'}
			id="tag_tabs"
		>
			<Box sx={getTabsListWrapStyles({ tabRowVariant, isSmallScreen })}>
				<Box
					sx={getTabsWrapperStyles({
						tabRowVariant,
						isTabsFixed,
						headerPositioning,
						withDisappearingHeader,
						desktopHeaderDisappearingTopStyle,
						mobileHeaderDisappearingTopStyle,
					})}
				>
					<Tabs
						value={currentValue || false}
						onChange={handleChange}
						centered={centered}
						orientation="vertical"
						variant={isBelowMediumScreen ? 'scrollable' : 'fullWidth'}
						sx={tabsStyles}
						scrollButtons={false}
					>
						{tabs.map(({ label, href, value, jumplinkName }) => (
							<Tab
								key={value}
								sx={getTabStyles({ tabsSize, tabRowVariant, isSmallScreen })}
								{...a11yProps(value)}
								{...(href ? { href } : {})}
								value={value}
								label={label}
								data-test-id={`${dataTestId}_${value}`}
								onClick={() => jumplinkName && handleJumplink(jumplinkName)}
							/>
						))}
					</Tabs>
					<Box
						sx={{
							width: '4px', // Desired thickness of the line
							bgcolor: activeColor, // Desired color of the line
							transition: 'background-color 0.5s ease',
						}}
					/>
				</Box>
			</Box>
		</Box>
	);
}
